import { privatePageMetadata } from "@/lib/seo";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Camera, ImagePlus, MessagesSquare, PenSquare, Send, Upload, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmojiPicker } from "@/components/emoji-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications, notificationTitle } from "@/hooks/useNotifications";
import { compressChatImage } from "@/lib/image-compress";
import { uploadImage } from "@/lib/uploads";
import { findMessageRecipient, listMyConversations } from "@/lib/messaging";
import { startConversation } from "@/lib/messaging.functions";
import { useT } from "@/i18n";

type Search = { c?: string | undefined; to?: string | undefined };

const SYSTEM_ID = "__system";

export const Route = createFileRoute("/app/messages")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    c: typeof search['c'] === "string" ? search['c'] : undefined,
    to: typeof search['to'] === "string" ? search['to'] : undefined,
  }),
  head: () =>
    privatePageMetadata(
      "/app/messages",
      "Messages | BeHot.Now",
      "Your private BeHot.Now conversations.",
    ),
  component: MessagesPage,

});

type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  attachment_url: string | null;
  created_at: string;
};

function initials(name: string) {
  return name.trim().slice(0, 2).toUpperCase();
}

function time(value: string) {
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MessagesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const notifications = useNotifications(50);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<{ file: File; preview: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [handle, setHandle] = useState("");
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const {
    data: conversations = [],
    isLoading,
    isError: conversationsFailed,
    refetch: refetchConversations,
  } = useQuery({
    queryKey: ["conversations", user?.id],
    enabled: Boolean(user),
    queryFn: () => {
      if (!user) return [];
      return listMyConversations(user.id);
    },
    retry: 1,
  });

  // ?to=<userId> opens (or reuses) a direct thread with that member.
  useEffect(() => {
    if (!user || !search.to) return;
    const recipientId = search.to;
    let cancelled = false;
    void (async () => {
      try {
        const { conversationId } = await startConversation({ data: { recipientId } });
        if (cancelled) return;
        await queryClient.invalidateQueries({ queryKey: ["conversations", user.id] });
        void navigate({ to: "/app/messages", search: { c: conversationId }, replace: true });
      } catch {
        void navigate({ to: "/app/messages", search: {}, replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, search.to, navigate, queryClient]);

  const activeId = search.c ?? conversations[0]?.id ?? SYSTEM_ID;
  const isSystem = activeId === SYSTEM_ID;
  const active = useMemo(
    () => conversations.find((c) => c.id === activeId),
    [conversations, activeId],
  );

  const { data: messages = [] } = useQuery({
    queryKey: ["messages", activeId],
    enabled: Boolean(activeId) && !isSystem,
    queryFn: async (): Promise<Message[]> => {
      const { data, error: err } = await supabase
        .from("messages")
        .select("id,conversation_id,sender_id,body,attachment_url,created_at")
        .eq("conversation_id", activeId)
        .order("created_at", { ascending: true })
        .limit(300);
      if (err) throw err;
      return (data ?? []) as Message[];
    },
  });

  // Live updates for the open thread.
  useEffect(() => {
    if (!activeId || isSystem) return;
    // Keep the topic unique across Strict Mode effect remounts while the old
    // channel is still being removed asynchronously.
    const channelTopic = `thread:${activeId}:${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelTopic)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${activeId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ["messages", activeId] });
          void queryClient.invalidateQueries({ queryKey: ["conversations", user?.id] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [activeId, isSystem, queryClient, user?.id]);

  // Mark the open thread as read.
  useEffect(() => {
    if (!activeId || isSystem || !user) return;
    void (async () => {
      await supabase
        .from("conversation_participants")
        .update({ last_read_at: new Date().toISOString() })
        .eq("conversation_id", activeId)
        .eq("user_id", user.id);
      await queryClient.invalidateQueries({ queryKey: ["conversations", user.id] });
    })();
  }, [activeId, isSystem, messages.length, user, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, activeId, notifications.items.length]);

  async function pickFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      const compressed = await compressChatImage(file);
      setPending({ file: compressed, preview: URL.createObjectURL(compressed) });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("messaging.attachError"));
    }
  }

  async function send() {
    const body = draft.trim();
    if ((!body && !pending) || !activeId || isSystem || !user) return;
    setSending(true);
    setError(null);
    const keepDraft = draft;
    setDraft("");
    try {
      let attachmentUrl: string | null = null;
      if (pending) {
        attachmentUrl = await uploadImage(pending.file, user.id, "chat");
      }
      const { error: err } = await supabase.from("messages").insert({
        conversation_id: activeId,
        sender_id: user.id,
        body: body || "📷",
        attachment_url: attachmentUrl,
      });
      if (err) throw err;
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: ["messages", activeId] });
      await queryClient.invalidateQueries({ queryKey: ["conversations", user.id] });
    } catch (e) {
      setDraft(keepDraft);
      setError(e instanceof Error ? e.message : t("messaging.sendError"));
    } finally {
      setSending(false);
    }
  }

  async function startByHandle() {
    if (!handle.trim()) return;
    setComposeBusy(true);
    setComposeError(null);
    try {
       if (!user) return;
       const member = await findMessageRecipient(handle, user.id);
      if (!member) {
        setComposeError(t("messaging.notFound"));
        return;
      }
      const { conversationId } = await startConversation({ data: { recipientId: member.id } });
      await queryClient.invalidateQueries({ queryKey: ["conversations", user?.id] });
      setComposeOpen(false);
      setHandle("");
      void navigate({ to: "/app/messages", search: { c: conversationId } });
    } catch (e) {
      setComposeError(e instanceof Error ? e.message : t("messaging.notFound"));
    } finally {
      setComposeBusy(false);
    }
  }

  const systemUnread = notifications.unread;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold">{t("messaging.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("messaging.intro")}</p>
        </div>
        <Button variant="hot" size="sm" onClick={() => setComposeOpen(true)}>
          <PenSquare className="mr-2 size-4" />
          {t("messaging.newMessage")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <Card className="max-h-[70vh] overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => void navigate({ to: "/app/messages", search: { c: SYSTEM_ID } })}
            className={`flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted ${
              isSystem ? "bg-muted" : ""
            }`}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Bell className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold">
                  {t("messaging.systemThread")}
                </span>
                {systemUnread > 0 && (
                  <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                    {systemUnread}
                  </span>
                )}
              </span>
              <span className="line-clamp-1 block text-xs text-muted-foreground">
                {notifications.items[0]
                  ? notificationTitle(notifications.items[0], t)
                  : t("messaging.noNotifications")}
              </span>
            </span>
          </button>

          {isLoading && (
            <p className="p-4 text-sm text-muted-foreground">{t("messaging.loading")}</p>
          )}
          {conversationsFailed && (
            <div className="space-y-2 p-4">
              <p className="text-sm text-destructive">{t("messaging.sendError")}</p>
              <Button variant="outline" size="sm" onClick={() => void refetchConversations()}>
                {t("messaging.loading")}
              </Button>
            </div>
          )}
          {!isLoading && !conversationsFailed && conversations.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">{t("messaging.noThreads")}</p>
          )}
          {conversations.map((c) => {
            const peer = c.peers[0];
            const name = peer?.displayName ?? t("messaging.member");
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => void navigate({ to: "/app/messages", search: { c: c.id } })}
                className={`flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted ${
                  c.id === activeId ? "bg-muted" : ""
                }`}
              >
                <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {peer?.avatarUrl ? (
                    <img src={peer.avatarUrl} alt="" className="size-full object-cover" />
                  ) : (
                    initials(name)
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{name}</span>
                    {c.unread > 0 && (
                      <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                        {c.unread}
                      </span>
                    )}
                  </span>
                  <span className="line-clamp-1 block text-xs text-muted-foreground">
                    {c.subject ?? c.lastMessagePreview ?? t("messaging.noMessagesYet")}
                  </span>
                </span>
              </button>
            );
          })}
        </Card>

        <Card className="flex max-h-[70vh] min-h-[420px] flex-col">
          {isSystem && (
            <>
              <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div>
                  <p className="font-semibold">{t("messaging.systemThread")}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("messaging.notificationsIntro")}
                  </p>
                </div>
                {systemUnread > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => void notifications.markAllRead()}>
                    {t("messaging.markAllRead")}
                  </Button>
                )}
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto bg-muted/30 px-4 py-4">
                {notifications.items.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t("messaging.noNotifications")}</p>
                )}
                {[...notifications.items].reverse().map((n) => (
                  <div key={n.id} className="flex justify-start">
                    <div className="max-w-[80%] rounded-2xl rounded-bl-md bg-card px-3 py-2 text-sm shadow-sm">
                      <p className="font-semibold">{notificationTitle(n, t)}</p>
                      {n.body && <p className="mt-0.5 text-muted-foreground">{n.body}</p>}
                      {n.url && (
                        <a href={n.url} className="mt-1 block text-xs font-semibold text-primary">
                          {t("messaging.open")}
                        </a>
                      )}
                      <p className="mt-1 text-[10px] text-muted-foreground">{time(n.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
            </>
          )}

          {!isSystem && !active && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              <MessagesSquare className="size-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t("messaging.selectThread")}</p>
            </div>
          )}

          {!isSystem && active && (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {active.peers[0]?.avatarUrl ? (
                    <img src={active.peers[0].avatarUrl} alt="" className="size-full object-cover" />
                  ) : (
                    initials(active.peers[0]?.displayName ?? t("messaging.member"))
                  )}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-semibold">
                    {active.peers[0]?.displayName ?? t("messaging.member")}
                  </p>
                  {active.subject && (
                    <p className="truncate text-xs text-muted-foreground">{active.subject}</p>
                  )}
                </div>
              </div>

              <div className="flex-1 space-y-1.5 overflow-y-auto bg-muted/30 px-4 py-4">
                {messages.map((m, i) => {
                  const mine = m.sender_id === user?.id;
                  const prev = messages[i - 1];
                  const grouped = prev?.sender_id === m.sender_id;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${mine ? "justify-end" : "justify-start"} ${
                        grouped ? "" : "pt-2"
                      }`}
                    >
                      <div
                        className={`max-w-[78%] px-3 py-2 text-sm shadow-sm ${
                          mine
                            ? "rounded-2xl rounded-br-md bg-primary text-primary-foreground"
                            : "rounded-2xl rounded-bl-md bg-card text-foreground"
                        }`}
                      >
                        {m.attachment_url && (
                          <a href={m.attachment_url} target="_blank" rel="noreferrer">
                            <img
                              src={m.attachment_url}
                              alt=""
                              loading="lazy"
                              className="mb-1 max-h-64 w-full rounded-xl object-cover"
                            />
                          </a>
                        )}
                        {m.body && m.body !== "📷" && (
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                        )}
                        <p className="mt-1 text-right text-[10px] opacity-70">
                          {time(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {(pending || error) && (
                <div className="flex items-center gap-3 border-t border-border px-3 py-2">
                  {pending && (
                    <div className="relative">
                      <img
                        src={pending.preview}
                        alt=""
                        className="size-14 rounded-lg object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setPending(null)}
                        aria-label={t("messaging.removePhoto")}
                        className="absolute -right-2 -top-2 rounded-full bg-foreground p-0.5 text-background"
                      >
                        <X className="size-3" />
                      </button>
                    </div>
                  )}
                  {error && <p className="text-xs text-destructive">{error}</p>}
                </div>
              )}

              <div className="flex items-end gap-2 border-t border-border p-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    void pickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    void pickFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={t("messaging.attachPhoto")}>
                      <ImagePlus className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuItem onSelect={() => cameraRef.current?.click()}>
                      <Camera className="mr-2 size-4" />
                      {t("messaging.takePhoto")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => fileRef.current?.click()}>
                      <Upload className="mr-2 size-4" />
                      {t("messaging.uploadFile")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <EmojiPicker
                  ariaLabel={t("messaging.addEmoji")}
                  onSelect={(emoji) => setDraft((d) => (d + emoji).slice(0, 4000))}
                />

                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  rows={1}
                  maxLength={4000}
                  placeholder={t("messaging.placeholder")}
                  className="min-h-[44px] resize-none rounded-2xl"
                />
                <Button
                  variant="hot"
                  size="icon"
                  disabled={sending || (!draft.trim() && !pending)}
                  onClick={() => void send()}
                  aria-label={t("messaging.send")}
                >
                  <Send className="size-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("messaging.newMessage")}</DialogTitle>
            <DialogDescription>{t("messaging.newMessageHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@handle"
              maxLength={80}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void startByHandle();
                }
              }}
            />
            {composeError && <p className="text-xs text-destructive">{composeError}</p>}
            <Button
              variant="hot"
              className="w-full"
              disabled={composeBusy || !handle.trim()}
              onClick={() => void startByHandle()}
            >
              {t("messaging.startChat")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
